import { useEffect, useState } from "react";
import { Download, Search, Users } from "lucide-react";
import { HomeownerListRow } from "components/HomeownerListRow";
import { HomeownerDetailModal } from "components/HomeownerDetailsCard";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { CallAPIGetPromise } from "Common/commaon";
import moment from "moment";
import { HomeownerRowShimmer } from "components/shimmer";
import { downloadCsv } from "helpers/csvExport";
import "./all-homeowners.css";

interface Homeowner {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  zipCode: string;
  profileImage: string;
  totalLeads: number;
  activeLeads: number;
  completedProjects: number;
  contractsCreated: number;
  contractorContacted: number;
  contractsSigned: number;
  status: "active" | "blocked" | "pending";
  joinedDate: string;
  lastActive: string;
}


type FilterType = "all" | "active" | "inactive" | "new" | "high-activity";

export default function HomeownerListAdmin() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedHomeowner, setSelectedHomeowner] = useState<Homeowner | null>(null);
  const [homeownersList,setHomeownersLst]=useState<Homeowner[] >([])
  const [startDate, setStartDate] = useState<string>("");
  const [refresh, setRefresh] = useState(false);
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const filterButtons: { label: string; value: FilterType }[] = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
    { label: "New Users", value: "new" },
    { label: "High Activity", value: "high-activity" },
  ];


  const getFilteredHomeowners = () => {
    let filtered = [...homeownersList];
  
    const now = moment();
  
    // Status filters
    if (activeFilter === "active") {
      filtered = filtered.filter((h) => h.status === "active");
  
    } else if (activeFilter === "inactive") {
      filtered = filtered.filter((h) => h.status !== "active");
  
    } else if (activeFilter === "new") {
      filtered = filtered.filter((h) =>
        moment(h.joinedDate).isAfter(now.clone().subtract(60, "days"))
      );
  
    } else if (activeFilter === "high-activity") {
      const maxLeads = Math.max(...homeownersList.map(h => h.totalLeads || 0));
      const threshold = maxLeads * 0.7;
  
      filtered = filtered.filter((h) => (h.totalLeads || 0) >= threshold);
    }
  
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
  
      filtered = filtered.filter((h) =>
        h.name?.toLowerCase().includes(q) ||
        h.email?.toLowerCase().includes(q) ||
        h.userId?.toLowerCase().includes(q) ||
        h.city?.toLowerCase().includes(q)
      );
    }
  
    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter((h) => {
        const joined = moment(h.joinedDate);
        if (!joined.isValid()) return false;

        const isAfterStart = startDate
          ? joined.isSameOrAfter(moment(startDate), "day")
          : true;
        const isBeforeEnd = endDate
          ? joined.isSameOrBefore(moment(endDate), "day")
          : true;

        return isAfterStart && isBeforeEnd;
      });
    }
  
    // Sort newest first
    filtered.sort((a, b) =>
      moment(b.joinedDate).valueOf() - moment(a.joinedDate).valueOf()
    );
  
    return filtered;
  };

  const filteredHomeowners = getFilteredHomeowners();

  const handleExportCsv = () => {
    downloadCsv("homeowners-report.csv", filteredHomeowners, [
      { header: "S.No", value: (_homeowner, index) => index + 1 },
      { header: "ID", value: (homeowner) => homeowner.id },
      { header: "User ID", value: (homeowner) => homeowner.userId },
      { header: "Name", value: (homeowner) => homeowner.name },
      { header: "Email", value: (homeowner) => homeowner.email },
      { header: "Phone", value: (homeowner) => homeowner.phone },
      { header: "City", value: (homeowner) => homeowner.city },
      { header: "State", value: (homeowner) => homeowner.state },
      { header: "Zip Code", value: (homeowner) => homeowner.zipCode },
      { header: "Status", value: (homeowner) => homeowner.status },
      { header: "Total Leads", value: (homeowner) => homeowner.totalLeads },
      { header: "Active Leads", value: (homeowner) => homeowner.activeLeads },
      { header: "Completed Projects", value: (homeowner) => homeowner.completedProjects },
      { header: "Contracts Created", value: (homeowner) => homeowner.contractsCreated },
      { header: "Contractors Contacted", value: (homeowner) => homeowner.contractorContacted },
      { header: "Contracts Signed", value: (homeowner) => homeowner.contractsSigned },
      {
        header: "Joined Date",
        value: (homeowner) =>
          homeowner.joinedDate ? moment(homeowner.joinedDate).format("YYYY-MM-DD") : "",
      },
      {
        header: "Last Active",
        value: (homeowner) =>
          homeowner.lastActive ? moment(homeowner.lastActive).format("YYYY-MM-DD") : "",
      },
    ]);
  };

  useEffect(() => {
    let isCurrent = true;

    setLoading(true);
    setLoadError(false);
    CallAPIGetPromise("/api/crm/homeowners")
      .then((res: any) => {
        if (isCurrent) {
          setHomeownersLst(Array.isArray(res?.data) ? res.data : []);
        }
      })
      .catch(() => {
        if (isCurrent) setLoadError(true);
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [refresh]);
  return (
    <div className="page-content tailwind-prefix all-homeowners-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
  <div className="w-full px-6 py-4">
    <div className="flex items-center justify-between">
      
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 bg-green-600 rounded-lg flex items-center justify-center">
          <Users className="h-6 w-6 text-white" />
        </div>

        <div>
          <div className="text-gray-900 font-semibold">
            Homeowner List
          </div>
          <div className="text-gray-600 text-sm">
            Manage homeowners and their project requests
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          variant="outline"
          onClick={handleExportCsv}
          disabled={filteredHomeowners.length === 0}
          className="bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
        <div className="flex items-center gap-2">
        <span className="text-gray-600">
          Total Homeowners:
        </span>
        <span className="text-gray-900 font-semibold">
          {filteredHomeowners.length}
        </span>
        </div>
      </div>

    

    </div>

    <div className="space-y-4 mt-3">
            {/* Search Bar */}
            <div className="app-search-field">
              <Search className="app-field-icon" />
              <Input
                type="text"
                placeholder="Search homeowners..."
                aria-label="Search homeowners"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">

{/* Date Range */}
<div className="flex items-center gap-3 flex-wrap">
  <div className="flex items-center gap-2">
    <label htmlFor="homeowners-from-date" className="text-sm text-gray-500">From</label>
    <Input
      id="homeowners-from-date"
      type="date"
      value={startDate}
      max={endDate || undefined}
      onChange={(e) => setStartDate(e.target.value)}
      className="max-w-[160px]"
    />
  </div>

  <span className="text-gray-400 hidden sm:block">→</span>

  <div className="flex items-center gap-2">
    <label htmlFor="homeowners-to-date" className="text-sm text-gray-500">To</label>
    <Input
      id="homeowners-to-date"
      type="date"
      value={endDate}
      min={startDate || undefined}
      onChange={(e) => setEndDate(e.target.value)}
      className="max-w-[160px]"
    />
  </div>

  {(startDate || endDate) && (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        setStartDate("");
        setEndDate("");
      }}
      className="text-gray-500 hover:text-black"
    >
      Clear
    </Button>
  )}
</div>

{/* Filters */}
<div className="flex flex-wrap gap-2">
  {filterButtons.map((filter) => (
    <Button
      key={filter.value}
      variant={activeFilter === filter.value ? "default" : "outline"}
      size="sm"
      onClick={() => setActiveFilter(filter.value)}
      aria-pressed={activeFilter === filter.value}
      className={
        activeFilter === filter.value
          ? "bg-blue-600 hover:bg-blue-700"
          : "hover:bg-gray-50 hover:text-black"
      }
    >
      {filter.label}
    </Button>
  ))}
</div>

</div>
            </div>
  </div>
</div>

      {/* Main Content */}
      <div className=" py-6">
        {loading ? (
          <div className="space-y-3" role="status" aria-label="Loading homeowners">
            <span className="sr-only">Loading homeowners</span>
            {[1, 2, 3, 4, 5].map((item) => (
              <HomeownerRowShimmer key={item} />
            ))}
          </div>
        ) : loadError ? (
          <div className="homeowners-state-card" role="alert">
            <div className="text-gray-900 font-semibold">Homeowners could not be loaded</div>
            <div className="text-gray-500 text-sm mt-1">Please check your connection and try again.</div>
            <Button size="sm" className="mt-3" onClick={() => setRefresh((value) => !value)}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
          {filteredHomeowners.length > 0 && (
            <div className="homeowner-list-columns" aria-hidden="true">
              <span>Homeowner</span>
              <span>Email</span>
              <span>Location</span>
              <span className="text-center">Active leads</span>
              <span className="text-center">Status</span>
            </div>
          )}
          {filteredHomeowners.length > 0 ? (
            filteredHomeowners.map((homeowner) => (
              <HomeownerListRow
                key={homeowner.id}
                homeowner={homeowner}
                onClick={() => setSelectedHomeowner(homeowner)}
              />
            ))
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="text-gray-500 text-lg">No homeowners found</div>
              <div className="text-gray-400 text-sm mt-2">
                Try adjusting your search or filters
              </div>
            </div>
          )}
          </div>
        )}

      </div>

      {/* Detail Modal */}
      {selectedHomeowner && (
        <HomeownerDetailModal
          homeowner={selectedHomeowner}
          goBack={()=>{
            setRefresh((value) => !value)
            setSelectedHomeowner(null)
            setSearchQuery("")
          }}
          onClose={() => setSelectedHomeowner(null)}
        />
      )}
    </div>
  );
}
